import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  Menu as MenuIcon,
  Settings,
  QrCode,
  Eye,
  LogOut,
  CreditCard,
  LifeBuoy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon } from "lucide-react";
import { APP_VERSION, BUILD_HASH } from "@/version";


export const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);

  const isDashboard =
    pathname === '/admin' ||
    pathname === '/admin/';

  const mobilePageTitle =
    {
      '/admin/menu': 'Menü',
      '/admin/banners': 'Kampanyalar',
      '/admin/preview': 'Menü Ön İzleme',
      '/admin/qr-code': 'QR Kod',
      '/admin/support': 'Destek',
      '/admin/subscription': 'Abonelik',
      '/admin/profile': 'Ayarlar',
    }[pathname] || 'QR Menü';

  const isMobileOptimizedPage =
    isDashboard ||
    pathname === '/admin/menu' ||
    pathname === '/admin/banners' ||
    pathname === '/admin/qr-code' ||
    pathname === '/admin/support' ||
    pathname === '/admin/subscription' ||
    pathname === '/admin/profile';

  const handleMobileBack = () => {
    const routerHistoryIndex = Number(window.history.state?.idx);

    if (Number.isFinite(routerHistoryIndex) && routerHistoryIndex > 0) {
      navigate(-1);
      return;
    }

    navigate('/admin', { replace: true });
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Yönetim Paneli' },
    { path: '/admin/menu', icon: MenuIcon, label: 'Menü' },
    { label: "Kampanyalar", icon: ImageIcon, path: "/admin/banners" },
    { path: '/admin/preview', icon: Eye, label: 'Menü Ön İzleme' },
    { path: '/admin/qr-code', icon: QrCode, label: 'QR Kod' },
    // 🆕 Abonelik / ödeme sayfası
    { path: '/admin/support', icon: LifeBuoy, label: 'Destek Talebi' },
    { path: '/admin/subscription', icon: CreditCard, label: 'Abonelik' },
    { path: '/admin/profile', icon: Settings, label: 'Ayarlar' },
  ];

  return (
    <div className="min-h-screen min-h-[100dvh] bg-zinc-50 flex overflow-x-hidden">
      {/* 🟦 DESKTOP SIDEBAR */}
      <aside className="hidden md:flex w-64 bg-white border-r border-zinc-200 flex-col fixed top-0 bottom-0 left-0 z-30">
        <div className="p-6 border-b border-zinc-100">
          <Link to="/admin" className="flex items-center gap-2">
            <QrCode className="w-6 h-6" />
            <span
              className="font-serif font-bold text-lg"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              QR Menü
            </span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex min-h-11 items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-700 hover:bg-zinc-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-4 pb-2 text-center">
          <div className="text-xs text-zinc-400">
            v{APP_VERSION} • {BUILD_HASH}
          </div>
        </div>  
        <div className="p-4 border-t border-zinc-100">
          <Button
            variant="ghost"
            className="min-h-11 w-full justify-start text-zinc-700 hover:bg-zinc-100"
            onClick={logout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* 🟧 MOBILE TOP BAR */}
      <header
        className={`${isDashboard ? "hidden" : "flex"} md:hidden fixed top-0 left-0 z-40 h-14 w-full items-center justify-center border-b border-zinc-200 bg-white px-4`}
      >
        <button
          type="button"
          onClick={handleMobileBack}
          aria-label="Önceki sayfaya dön"
          className="absolute left-2 flex min-h-11 items-center gap-1 rounded-lg px-2 text-[15px] font-medium text-[#18181B] active:bg-zinc-100"
        >
          <span className="-mt-0.5 text-[28px] font-light leading-none">‹</span>
          <span>Geri</span>
        </button>

        <span className="max-w-[58%] truncate text-[17px] font-semibold text-[#18181B]">
          {mobilePageTitle}
        </span>

        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
          aria-expanded={mobileOpen}
          className="absolute right-2 flex h-11 w-11 items-center justify-center rounded-lg text-[#18181B] active:bg-zinc-100"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
      </header>

      {/* 🟩 MOBILE SIDEBAR OVERLAY */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* 🟦 MOBILE SIDEBAR BODY */}
        <aside
          className={`fixed top-0 bottom-0 left-0 z-50 w-64 overflow-y-auto bg-white border-r border-zinc-200 flex-col transform transition-transform md:hidden ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
        <div className="p-6 border-b border-zinc-100 flex justify-between items-center">
          <span className="font-serif font-bold text-lg">QR Menü</span>
          <button
            className="flex h-11 w-11 items-center justify-center rounded-lg text-zinc-500 active:bg-zinc-100"
            onClick={() => setMobileOpen(false)}
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex min-h-11 items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-700 hover:bg-zinc-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 pb-2 text-center">
          <div className="text-xs text-zinc-400">
            v{APP_VERSION} • {BUILD_HASH}
          </div>
        </div>

        <div className="p-4 border-t border-zinc-100">
          <Button
            variant="ghost"
            className="min-h-11 w-full justify-start text-zinc-700 hover:bg-zinc-100"
            onClick={logout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Dashboard native-like görünümünü korurken mobile menüyü erişilebilir tut */}
      {isDashboard && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
          aria-expanded={mobileOpen}
          className="fixed right-3 top-3 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 bg-white/95 text-[#18181B] shadow-sm backdrop-blur md:hidden"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
      )}

      {/* 🟣 MAIN CONTENT */}
      <main className="flex-1 min-w-0 flex flex-col overflow-x-hidden md:ml-64">
        {/* mobile için üstteki barın boşluğu */}
        {!isDashboard && <div className="h-14 shrink-0 md:hidden" />}
        <div
          className={`w-full min-w-0 ${
            isMobileOptimizedPage ? "px-0 py-0" : "px-3 py-4"
          } md:px-8 md:py-8`}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
